import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  Zap, ArrowLeft, Loader2, Wallet, TrendingUp, Clock, AlertTriangle,
  CheckCircle, DollarSign, CircleDollarSign,
} from "lucide-react";
import BrandLogo from "../components/BrandLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const axiosAuth = () => axios.create({
  withCredentials: true,
  headers: { Authorization: `Bearer ${localStorage.getItem("lf_session") || ""}` },
});

const fmtUSD = (n) => `$${(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
const fmtAmount = (amount, currency) => {
  const symbol = currency === "eur" ? "€" : "$";
  return `${symbol}${(amount || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
};
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—";

function StatCard({ icon: Icon, label, value, sub, tone = "default", testId }) {
  const tones = {
    default: "bg-white border-zinc-200",
    good: "bg-gradient-to-br from-emerald-50 to-white border-emerald-200",
    warning: "bg-gradient-to-br from-amber-50 to-white border-amber-200",
    bad: "bg-gradient-to-br from-red-50 to-white border-red-200",
  };
  const iconTones = {
    default: "bg-zinc-100 text-zinc-700",
    good: "bg-emerald-100 text-emerald-700",
    warning: "bg-amber-100 text-amber-700",
    bad: "bg-red-100 text-red-700",
  };
  return (
    <div data-testid={testId} className={`rounded-2xl p-5 border ${tones[tone]}`}>
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${iconTones[tone]}`}><Icon size={16} /></div>
        <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">{label}</span>
      </div>
      <div className="text-2xl font-extrabold text-zinc-950">{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

const STATUS_PILL = {
  paid: "bg-emerald-100 text-emerald-800",
  pending: "bg-amber-100 text-amber-800",
  unpaid: "bg-amber-100 text-amber-800",
  expired: "bg-red-100 text-red-800",
};

export default function AdminFinance() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [txns, setTxns] = useState([]);
  const [filter, setFilter] = useState({ status: "", payment_type: "" });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [s, t] = await Promise.all([
        axiosAuth().get(`${API}/admin/finance/summary`),
        axiosAuth().get(`${API}/admin/finance/transactions`, {
          params: {
            ...(filter.status ? { status: filter.status } : {}),
            ...(filter.payment_type ? { payment_type: filter.payment_type } : {}),
          },
        }),
      ]);
      setSummary(s.data);
      setTxns(t.data);
    } catch (e) {
      if (e.response?.status === 401) navigate("/admin/login");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.status, filter.payment_type]);

  if (loading) return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-finance">
      <header className="bg-white border-b border-zinc-200 px-6 py-4 flex items-center gap-3">
        <button onClick={() => navigate("/admin/dashboard")} className="p-2 hover:bg-zinc-100 rounded-lg" data-testid="btn-back-finance"><ArrowLeft size={16} /></button>
        <BrandLogo variant="mark" size={32} />
        <div className="h-3 w-32 bg-zinc-200 rounded animate-pulse" />
      </header>
      <div className="p-6"><div className="flex items-center justify-center h-64"><Loader2 size={28} className="animate-spin text-zinc-400" /></div></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-finance">
      <header className="bg-white border-b border-zinc-200 px-6 py-4 flex items-center gap-3">
        <button onClick={() => navigate("/admin/dashboard")} className="p-2 hover:bg-zinc-100 rounded-lg" data-testid="btn-back-finance"><ArrowLeft size={16} /></button>
        <div className="w-8 h-8 bg-zinc-950 rounded-lg flex items-center justify-center"><Wallet size={16} className="text-white" /></div>
        <div>
          <div className="font-bold text-zinc-900">Finance</div>
          <div className="text-xs text-zinc-500">Revenue, receivables & overdue tracking</div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Summary tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <StatCard icon={TrendingUp} label="Total Revenue" tone="good" testId="tile-revenue"
            value={fmtUSD(summary?.revenue?.total_paid_usd)}
            sub={`${summary?.counts?.paid_transactions} paid txns`} />
          <StatCard icon={CheckCircle} label="Upfront Paid" tone="good" testId="tile-upfront-paid"
            value={fmtUSD(summary?.revenue?.paid_upfront_usd)} />
          <StatCard icon={CheckCircle} label="Final Paid" tone="good" testId="tile-final-paid"
            value={fmtUSD(summary?.revenue?.paid_final_usd)} />
          <StatCard icon={Clock} label="Pending Receivables" tone="warning" testId="tile-pending"
            value={fmtUSD((summary?.receivables?.pending_upfront_usd || 0) + (summary?.receivables?.pending_final_usd || 0))}
            sub={`${summary?.counts?.pending_transactions} open txns`} />
          <StatCard icon={AlertTriangle} label="Overdue Final Reviews" tone="bad" testId="tile-overdue"
            value={summary?.counts?.overdue_final_reviews || 0}
            sub={`${summary?.counts?.projects_in_final_review} in Final Review`} />
        </div>

        {/* Overdue projects */}
        {summary?.overdue_projects?.length > 0 && (
          <div className="bg-white border border-red-200 rounded-2xl overflow-hidden" data-testid="overdue-projects-card">
            <div className="px-5 py-4 border-b border-red-100 bg-red-50 flex items-center gap-2">
              <AlertTriangle size={16} className="text-red-600" />
              <span className="font-semibold text-red-900 text-sm">Overdue Final Payments — {summary.overdue_projects.length}</span>
            </div>
            <table className="w-full text-sm">
              <thead><tr className="border-b border-zinc-100">
                {["Project", "Client", "Amount Due", "Deadline", "Days Overdue", ""].map(h => (
                  <th key={h} className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wide text-zinc-500">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {summary.overdue_projects.map(o => (
                  <tr key={o.project_id} data-testid={`overdue-row-${o.project_id}`} className="border-b border-zinc-50 hover:bg-red-50/30">
                    <td className="px-5 py-3 font-mono text-xs text-zinc-700">{o.project_id}</td>
                    <td className="px-5 py-3"><div className="font-medium text-zinc-900">{o.client_name}</div><div className="text-xs text-zinc-500">{o.client_email}</div></td>
                    <td className="px-5 py-3 font-bold text-zinc-950">{fmtAmount(o.amount_due_usd * (o.currency === "eur" ? 0.92 : 1), o.currency)}</td>
                    <td className="px-5 py-3 text-xs text-zinc-600">{fmtDate(o.deadline)}</td>
                    <td className="px-5 py-3"><span className="inline-flex items-center px-2 py-0.5 rounded-full bg-red-100 text-red-800 text-xs font-bold">{o.days_overdue}d overdue</span></td>
                    <td className="px-5 py-3"><Link to={`/admin/projects/${o.project_id}`} className="text-xs text-blue-600 hover:underline">Open →</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Transactions ledger */}
        <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-zinc-100 flex items-center gap-2 flex-wrap">
            <CircleDollarSign size={16} className="text-zinc-700" />
            <span className="font-semibold text-zinc-900 text-sm">Transactions</span>
            <div className="ml-auto flex gap-2 flex-wrap">
              <select data-testid="filter-payment-status" value={filter.status} onChange={e => setFilter(f => ({ ...f, status: e.target.value }))}
                className="px-3 py-1.5 text-xs border border-zinc-200 rounded-full bg-zinc-50 focus:outline-none">
                <option value="">All statuses</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
                <option value="expired">Expired</option>
              </select>
              <select data-testid="filter-payment-type" value={filter.payment_type} onChange={e => setFilter(f => ({ ...f, payment_type: e.target.value }))}
                className="px-3 py-1.5 text-xs border border-zinc-200 rounded-full bg-zinc-50 focus:outline-none">
                <option value="">All types</option>
                <option value="upfront_50">Upfront 50%</option>
                <option value="final_50">Final 50%</option>
              </select>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-zinc-100">
                {["Date", "Client", "Project", "Type", "Amount", "Status", ""].map(h => (
                  <th key={h} className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wide text-zinc-500">{h}</th>
                ))}
              </tr></thead>
              <tbody data-testid="transactions-table">
                {txns.length === 0 ? (
                  <tr><td colSpan={7} className="px-5 py-10 text-center text-zinc-400">No transactions</td></tr>
                ) : txns.map(t => (
                  <tr key={t.stripe_session_id} data-testid={`txn-row-${t.stripe_session_id}`} className="border-b border-zinc-50 hover:bg-zinc-50">
                    <td className="px-5 py-3 text-xs text-zinc-500">{fmtDate(t.created_at)}</td>
                    <td className="px-5 py-3"><div className="font-medium text-zinc-900">{t.client_name || "—"}</div><div className="text-xs text-zinc-500">{t.client_email || ""}</div></td>
                    <td className="px-5 py-3"><Link to={`/admin/projects/${t.project_id}`} className="font-mono text-xs text-blue-600 hover:underline">{t.project_id}</Link></td>
                    <td className="px-5 py-3"><span className="text-xs font-semibold text-zinc-700">{t.payment_type === "upfront_50" ? "Upfront 50%" : "Final 50%"}</span></td>
                    <td className="px-5 py-3 font-bold text-zinc-950">{fmtAmount(t.amount, t.currency)}</td>
                    <td className="px-5 py-3"><span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-bold ${STATUS_PILL[t.payment_status] || "bg-zinc-100 text-zinc-700"}`}>{t.payment_status}</span></td>
                    <td className="px-5 py-3 text-xs text-zinc-400 font-mono max-w-xs truncate">{t.stripe_session_id?.slice(0, 14)}…</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
